import { create } from 'zustand';
import {
    TrussUsers,
    fetchUserDataFromSupabase,
    updateUserDataInSupabase,
} from '../services/supabase';
import type { SecurityInterests } from '../shared/securityInterests';

export type UserProfileUpdates = {
    username?: string;
    bio?: string;
    avatarUrl?: string;
    organization?: string;
    securityInterests?: SecurityInterests;
    likedAuthors?: string[];
    likedSources?: string[];
};

interface UserDataStore {
    userData: TrussUsers;
    isLoading: boolean;
    error: string | null;
    paymentStatus: string | null;
    refreshUserData: () => Promise<void>;
    fetchUserData: () => Promise<void>;
    updateUserData: (updates: UserProfileUpdates) => Promise<void>;
    resetUserData: () => void;
    setPaymentStatus: (status: string | null) => void;
}

const initialUserData: TrussUsers = {
    id: '', 
    username: '',
    bio: '',
    api_key: null,
    api_key_id: null,
    avatar_url: '',
    role: 'community',
    filters: [],
    charts: [],
    subscriptions: [],
    liked_authors: [],
    liked_products: [],
    liked_sources: [],
};

export const useUserDataStore = create<UserDataStore>((set) => ({
    // Initial state
    userData: initialUserData,
    isLoading: false,
    error: null,
    paymentStatus: null,
    refreshUserData: async () => {
        set({ isLoading: true, error: null });
        try {
            const freshData = await fetchUserDataFromSupabase();
            set(state => ({ 
                userData: {
                    ...state.userData,
                    ...freshData
                },
                isLoading: false 
            }));
        } catch (error) {
            console.error('Error refreshing user data:', error);
            set({ isLoading: false });
            throw error;
        }
    },

    // Actions
    fetchUserData: async () => {
        set({ isLoading: true, error: null });
        try {
            const userData = await fetchUserDataFromSupabase();
            set({ 
                userData: {
                    ...initialUserData,
                    ...userData,
                }, 
                isLoading: false 
            });
        } catch (error: any) {
            console.error('Detailed error:', error);
            set({ 
                error: error.message, 
                isLoading: false 
            });
        }
    },

    updateUserData: async (updates) => {
        set({ isLoading: true, error: null });
        try {
            const data = await updateUserDataInSupabase(updates);
            set((state) => ({
                userData: { ...state.userData, ...data },
                isLoading: false,
            }));
        } catch (error: any) {
            set({
                error: error.message,
                isLoading: false,
            });
            throw error;
        }
    },

    setPaymentStatus: (status: string | null) => {
        set({ paymentStatus: status });
    },

    resetUserData: () => {
        set({ 
            userData: initialUserData,
            error: null,
            isLoading: false,
            paymentStatus: null
        });
    }
}));
